import { useEffect, useMemo, useRef, useState, type ChangeEvent } from 'react';
import type { SelectChangeEvent } from '@mui/material';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import { Search as SearchIcon, Layers } from '@mui/icons-material';
import { useDataContext } from '@/lib/DataContext';
import { ROLE } from '@/lib/constants';
import sentinelNetwork from '@/lib/api/sentinelNetwork';
import { deleteProjects, runBulkScan } from '@/lib/api/scanService';
import TypedConfirmDialog from '@/components/ui/TypedConfirmDialog';

type AdminProject = {
  appId: string;
  appName: string;
  leanIXId?: string;
  created_date?: string;
};

const DEBOUNCE_MS = 300;
const ROWS_PER_PAGE_DEFAULT = 25;

const OPERATIONS = [
  { value: '', label: 'Choose operation' },
  { value: 'delete', label: 'Delete projects' },
  { value: 'sonar', label: 'Run Sonar scan' },
  { value: 'blackduck', label: 'Run Black Duck scan' },
  { value: 'trivy', label: 'Run Trivy scan' },
  { value: 'polaris', label: 'Run Polaris scan' },
  { value: 'ai-kpi', label: 'Run Delta AI KPI scan' },
];

export default function AdminProjectsPage() {
  const theme = useTheme();
  const { data } = useDataContext() || {};
  const isAdmin = data?.roles?.includes(ROLE.SENTINEL_POWER_USER);

  const [projects, setProjects] = useState<AdminProject[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(ROWS_PER_PAGE_DEFAULT);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [operation, setOperation] = useState('');
  const [running, setRunning] = useState(false);
  const [pending, setPending] = useState<{
    open: boolean;
    word: 'delete' | 'run';
    operation: string;
    label: string;
  } | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!isAdmin) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(0);
    }, DEBOUNCE_MS);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [search, isAdmin]);

  useEffect(() => {
    if (!isAdmin) return;
    fetchProjects();
  }, [debouncedSearch, isAdmin]);

  const fetchProjects = async () => {
    try {
      setLoading(true);
      const params: Record<string, string> = { lean: 'true' };
      if (debouncedSearch) params.search = debouncedSearch;
      const res = await sentinelNetwork.get('/config/projects', { params });
      const list: AdminProject[] = res.data?.data || [];
      setProjects(list);
      // Remove selected entries that no longer exist in the filtered list.
      const existingIds = new Set(list.map((p: AdminProject) => p.appId));
      setSelected((prev: Set<string>) => {
        const next = new Set<string>(prev);
        for (const id of next) if (!existingIds.has(id)) next.delete(id);
        return next;
      });
    } catch (err: any) {
      console.error('Failed to fetch projects:', err);
      setSnackbar({ open: true, message: err.response?.data?.message || 'Failed to load projects', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const filteredProjects = useMemo(() => {
    const term = debouncedSearch.toLowerCase();
    if (!term) return projects;
    return projects.filter(
      (p: AdminProject) =>
        (p.appName || '').toLowerCase().includes(term) ||
        (p.appId || '').toLowerCase().includes(term) ||
        (p.leanIXId || '').toLowerCase().includes(term)
    );
  }, [projects, debouncedSearch]);

  const pagedProjects = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredProjects.slice(start, start + rowsPerPage);
  }, [filteredProjects, page, rowsPerPage]);

  const allPageSelected = pagedProjects.length > 0 && pagedProjects.every((p: AdminProject) => selected.has(p.appId));
  const somePageSelected = pagedProjects.some((p: AdminProject) => selected.has(p.appId)) && !allPageSelected;

  const handleToggle = (appId: string) => {
    setSelected((prev: Set<string>) => {
      const next = new Set<string>(prev);
      if (next.has(appId)) next.delete(appId);
      else next.add(appId);
      return next;
    });
  };

  const handleSelectAll = () => {
    setSelected((prev: Set<string>) => {
      const next = new Set<string>(prev);
      if (allPageSelected) {
        pagedProjects.forEach((p: AdminProject) => next.delete(p.appId));
      } else {
        pagedProjects.forEach((p: AdminProject) => next.add(p.appId));
      }
      return next;
    });
  };

  const handleChangePage = (_: unknown, newPage: number) => setPage(newPage);
  const handleChangeRowsPerPage = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const selectedProjects = useMemo(() => {
    const map = new Map<string, AdminProject>(projects.map((p: AdminProject) => [p.appId, p]));
    return Array.from(selected as Set<string>)
      .map((id: string) => map.get(id))
      .filter((p): p is AdminProject => Boolean(p));
  }, [selected, projects]);

  const handleRun = () => {
    if (!operation || selected.size === 0) return;
    const op = OPERATIONS.find((o) => o.value === operation);
    if (!op) return;
    setPending({
      open: true,
      word: operation === 'delete' ? 'delete' : 'run',
      operation,
      label: op.label,
    });
  };

  const runOperation = async () => {
    if (!pending?.operation) return;
    const appIds = Array.from(selected as Set<string>);
    setRunning(true);
    try {
      if (pending.operation === 'delete') {
        const res = await deleteProjects(appIds);
        setSnackbar({ open: true, message: res.data?.message || `Deleted ${appIds.length} project(s)`, severity: 'success' });
      } else {
        const res = await runBulkScan(pending.operation, appIds);
        setSnackbar({ open: true, message: res.data?.message || `${pending.operation} scan queued for ${appIds.length} project(s)`, severity: 'success' });
      }
      setSelected(new Set());
      await fetchProjects();
    } catch (err: any) {
      console.error('Bulk operation failed:', err);
      setSnackbar({ open: true, message: err.response?.data?.message || 'Bulk operation failed', severity: 'error' });
    } finally {
      setRunning(false);
      setPending(null);
    }
  };

  if (!isAdmin) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100%">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2.5}>
        <Box>
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 0.5 }}>
            <Box sx={{ p: 1, borderRadius: 2, bgcolor: alpha(theme.palette.primary.main, 0.1), display: 'flex' }}>
              <Layers sx={{ color: theme.palette.primary.main }} />
            </Box>
            <Typography variant="h5" fontWeight={300}>
              Project Control
            </Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary" fontSize="0.875rem">
            Manage all Sentinel projects — search, select, and run bulk actions.
          </Typography>
        </Box>

        <Stack direction="row" spacing={1.5} alignItems="center">
          <FormControl size="small" sx={{ minWidth: 220 }} disabled={running}>
            <InputLabel>Operation</InputLabel>
            <Select value={operation} label="Operation" onChange={(e: SelectChangeEvent<string>) => setOperation(e.target.value)}>
              {OPERATIONS.map((op) => (
                <MenuItem key={op.value} value={op.value}>
                  {op.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            variant="contained"
            size="small"
            disabled={!operation || selected.size === 0 || running}
            onClick={handleRun}
            sx={{ textTransform: 'none' }}
          >
            {running ? <CircularProgress size={16} color="inherit" /> : 'Run'}
          </Button>
        </Stack>
      </Stack>

      <TextField
        placeholder="Search projects..."
        size="small"
        value={search}
        onChange={(e: ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)}
        sx={{ mb: 2, width: 360 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
        }}
      />

      <Card variant="outlined" elevation={0}>
        <CardContent sx={{ p: 0 }}>
          <TableContainer component={Paper} elevation={0} sx={{ maxHeight: 'calc(100vh - 340px)' }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox indeterminate={somePageSelected} checked={allPageSelected} onChange={handleSelectAll} disabled={pagedProjects.length === 0 || running} />
                  </TableCell>
                  <TableCell><strong>App Name</strong></TableCell>
                  <TableCell><strong>App ID</strong></TableCell>
                  <TableCell><strong>LeanIX ID</strong></TableCell>
                  <TableCell><strong>Created</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pagedProjects.map((project: AdminProject) => (
                  <TableRow
                    key={project.appId}
                    hover
                    onClick={() => !running && handleToggle(project.appId)}
                    selected={selected.has(project.appId)}
                    sx={{ cursor: running ? 'default' : 'pointer' }}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox checked={selected.has(project.appId)} disabled={running} />
                    </TableCell>
                    <TableCell>{project.appName || '—'}</TableCell>
                    <TableCell>
                      <Typography variant="body2" fontFamily="monospace" fontSize={12}>
                        {project.appId}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {project.leanIXId ? (
                        <Chip label={project.leanIXId} size="small" variant="outlined" />
                      ) : (
                        '—'
                      )}
                    </TableCell>
                    <TableCell>
                      {project.created_date
                        ? new Date(project.created_date).toLocaleDateString('en-GB', {
                            day: '2-digit',
                            month: 'short',
                            year: 'numeric',
                          })
                        : '—'}
                    </TableCell>
                  </TableRow>
                ))}
                {pagedProjects.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">
                        {loading ? 'Loading projects…' : 'No projects found'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={filteredProjects.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[10, 25, 50, 100]}
            showFirstButton
            showLastButton
          />
        </CardContent>
      </Card>

      <Stack direction="row" justifyContent="space-between" alignItems="center" mt={1}>
        <Typography variant="body2" color="text.secondary">
          {filteredProjects.length} total
          {selected.size > 0 && <> · <strong>{selected.size} selected</strong></>}
        </Typography>
        {operation && selected.size > 0 && (
          <Typography variant="body2" color="text.secondary">
            {OPERATIONS.find((o) => o.value === operation)?.label} for {selected.size} project{selected.size !== 1 ? 's' : ''}
          </Typography>
        )}
      </Stack>

      <TypedConfirmDialog
        open={pending?.open ?? false}
        title={
          pending?.operation === 'delete'
            ? `Delete ${selected.size} project${selected.size !== 1 ? 's' : ''}?`
            : `${pending?.label} for ${selected.size} project${selected.size !== 1 ? 's' : ''}?`
        }
        description={
          pending?.operation === 'delete'
            ? 'This cannot be undone. The following projects will be permanently deleted:'
            : 'This operation will be queued for the following projects:'
        }
        affectedItems={selectedProjects.map((p: AdminProject) => ({ id: p.appId, label: p.appName || p.appId }))}
        confirmWord={pending?.word ?? ''}
        confirmButtonText={pending?.operation === 'delete' ? 'Delete' : 'Run'}
        confirmButtonColor={pending?.operation === 'delete' ? 'error' : 'primary'}
        loading={running}
        onConfirm={runOperation}
        onCancel={() => setPending(null)}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
